import AnalysisToolService, {
  ICaptures
} from '../../services/AnalysisToolService';
import { Button, ClickAwayListener, IconButton } from '@mui/material';
import {
  LessonComponentResponseType,
  ScreenCaptureComponent
} from '../../models/LessonComponent';
import React, { useEffect, useRef, useState } from 'react';
import { cloneDeep, isArray, isEmpty, isNull } from 'lodash';

import AnalysisToolComponent from '../video_analysis_tool/AnalysisToolComponent';
import AssetService from '../../../shared/services/AssetService';
import { AssetType } from '../../../shared/models/Asset';
import AuthService from '../../../shared/services/AuthService';
import CancelIcon from '@mui/icons-material/Cancel';
import Constants from '../../constants';
import { CustomCarousel } from './CustomCarousel';
import { CustomLoadingProgress } from '../../../shared/components/CustomLoadingProgress';
import Grid from '@mui/material/Unstable_Grid2';
import LessonComponentService from '../../services/LessonComponentService';
import { ScreenCaptureItem } from '../../models/LessonComponentContent';
import { ScreenCaptureVideo } from './ScreenCaptureVideo';
import { nanoid } from 'nanoid';
import screenCaptureStyles from '../../styles/ScreenCapture.module.css';
import { useAssetStorage } from '../../../shared/hooks/useAssetStorage';
import useAssets from '../../../shared/hooks/useAssets';
import { useLessonComponentContext } from '../../models/LessonComponentContext';
import { useLessonContext } from '../../models/LessonContext';
import { useLessonPlayerContext } from '../../../dashboard/models/LessonPlayerContext';

interface ScreenCaptureProps {
  component: ScreenCaptureComponent;
  onChange: (response: LessonComponentResponseType) => void;
}

export const ScreenCapture = (props: ScreenCaptureProps) => {
  const { lessonState } = useLessonPlayerContext();
  const { correct, reattempt, answerSaved, userAnswer } =
    useLessonComponentContext();
  const { mode, finalized, quizLocked, assessmentSubmitted, portfolioLocked } =
    useLessonContext();
  const imageRef = useRef(null);
  const tool = useRef<AnalysisToolComponent>();
  const videoUrl = props.component.settings.assetkey || null;
  const learnerAnswer: ScreenCaptureItem[] = props.component.answer
    ? JSON.parse(props.component.answer)
    : null;
  const owner =
    mode === 'mark' ? lessonState?.answerParams.userkey : AuthService.Userkey;
  const { uploadCaptures } = useAssetStorage(AuthService.Tenant);
  const [deleteItems, setDeleteItems] = useState<string[]>([]);
  const [isItemCaptured, setIsItemCaptured] = useState(false);
  const [isItemDeleted, setIsItemDeleted] = useState(false);
  const [learnerItems, setLearnerItems] = useState<ScreenCaptureItem[]>([]);
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const [currentShowItem, setCurrentShowItem] = useState<ScreenCaptureItem>();
  const [capturing, setCapturing] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [goToIndex, setGoToIndex] = useState<number>();

  const type: AssetType[] = ['image'];
  const { deleteAssets } = useAssets(
    type,
    'learner',
    false /* no need to load assets */
  );

  useEffect(() => {
    if (answerSaved) {
      if (mode === 'learn') {
        uploadCaptures(learnerItems, 'learner_assets');
        deleteAssets(
          deleteItems?.map((item) => item),
          'learner'
        );
      }
    }
  }, [answerSaved]);

  /** set learner's saved images if any */
  useEffect(() => {
    if (
      (mode === 'learn' || mode === 'mark') &&
      !isItemCaptured &&
      !isItemDeleted &&
      learnerAnswer
    ) {
      const fetchProps: ICaptures = {
        // Earlier answer wasn't an array. It was an object.
        captures: isArray(learnerAnswer) ? learnerAnswer : [learnerAnswer],
        owner,
        setLoading,
        setItems: setLearnerItems
      };
      AnalysisToolService.fetchItems(fetchProps);
    }
  }, [mode, props.component.answer]);

  /** set the current pagination item */
  useEffect(() => {
    if (isItemCaptured && learnerItems?.length) {
      setGoToIndex(learnerItems.length);
      setCurrentItemIndex(learnerItems.length - 1);
    }
  }, [isItemCaptured, learnerItems?.length]);

  /* Once finalized(when user clicks ignore and continue without saving answers), set the learner items as in the database */
  useEffect(() => {
    if (finalized) {
      if (userAnswer?.length > 0) {
        const existingItems: ScreenCaptureItem[] = learnerItems.filter((item) =>
          userAnswer.includes(item.id)
        );
        setLearnerItems(existingItems);
      } else {
        setLearnerItems([]);
      }
    }
  }, [finalized, userAnswer]);

  /** Handle Screen Capture click */
  const handleCapture = async () => {
    setCapturing(true);
    setIsItemCaptured(true);
    setIsItemDeleted(false);
    const { base64, state } = await tool.current?.triggerCapture();
    const id = nanoid();
    const copyItems = [
      ...learnerItems,
      { id, image: base64, state: JSON.stringify(state) }
    ];
    setLearnerItems(copyItems);
    props.onChange(
      JSON.stringify(
        copyItems.map((item) => {
          return { id: item.id, state: item.state };
        })
      )
    );
    setCapturing(false);
  };

  /** Remove captured image
   * @param deleteIndex current index of the selected item
   */
  const handleRemove = (deleteIndex: number) => {
    const copyItems = cloneDeep(learnerItems);
    copyItems.splice(deleteIndex, 1);
    setLearnerItems(copyItems);
    if (learnerItems.length > 1) {
      let newIndex = currentItemIndex;
      if (
        currentItemIndex > deleteIndex ||
        (currentItemIndex === deleteIndex &&
          currentItemIndex === learnerItems.length - 1)
      ) {
        newIndex = currentItemIndex - 1;
      }
      setGoToIndex(newIndex);
      setCurrentItemIndex(newIndex);
    }
    setIsItemDeleted(true);
    setIsItemCaptured(false);
    const copyDeleteItems = [...deleteItems, learnerItems[deleteIndex]?.id];
    setDeleteItems(copyDeleteItems);
    if (currentItemIndex === deleteIndex) {
      tool.current?.hideImage();
      setCurrentShowItem(null);
    }
    props.onChange(
      copyItems.length
        ? JSON.stringify(
            copyItems.map((item) => {
              return { id: item.id, state: item.state };
            })
          )
        : null
    );
  };

  /** Set number of items display in the viewport */
  const breakPoints = [
    { width: 1, itemsToShow: 1 },
    { width: 280, itemsToShow: 2 },
    { width: 420, itemsToShow: 3 },
    { width: 560, itemsToShow: 4 }
  ];

  const handleImageClick = (item: ScreenCaptureItem, index: number) => {
    tool.current?.showImage(item.image);
    setCurrentItemIndex(index);
    setCurrentShowItem(item);
  };

  /** Handle mouse click away */
  const handleClickAway = (event: MouseEvent | TouchEvent) => {
    // It fires with 'touchend' and 'click' event on touch devices.
    // The 'touchend' fires before 'click',
    if (event.type === 'touchend') {
      return;
    }
    //Check the mouse click on current item's image or not
    const clickEventImage = event.target['alt'];
    if (
      mode !== 'mark' &&
      clickEventImage !== '' &&
      clickEventImage !== currentShowItem?.id
    ) {
      tool.current?.hideImage();
      setCurrentShowItem(null);
    }
  };

  const canEditCapture = () => {
    return (
      LessonComponentService.canSubmit(mode) &&
      !finalized &&
      !assessmentSubmitted &&
      !quizLocked
    );
  };

  return (
    <>
      {mode === 'edit' || !videoUrl ? (
        <ScreenCaptureVideo videoUrl={videoUrl} component={props.component} />
      ) : (
        <Grid
          container
          direction="column"
          justifyContent="center"
          alignItems="center"
        >
          <Grid className={screenCaptureStyles.videoTool}>
            <AnalysisToolComponent
              ref={tool}
              mediaUrl={AssetService.getAssetUrl(videoUrl)}
            />
          </Grid>
          {canEditCapture() && (
            <Grid className={screenCaptureStyles.captureButton}>
              <Button
                color="primary"
                variant="contained"
                disabled={
                  capturing ||
                  learnerItems?.length >= Constants.ScreenCaptureMaxNumItems ||
                  (!isNull(correct) && !reattempt) ||
                  portfolioLocked
                }
                onClick={handleCapture}
              >
                Screen Capture
              </Button>
            </Grid>
          )}
          {loading && <CustomLoadingProgress />}
          {!isEmpty(learnerItems) && (
            <Grid
              className={`${screenCaptureStyles.captureBlock} ${
                screenCaptureStyles.containerWidth
              } ${
                learnerItems?.length < 4
                  ? 'rec-item-wrapper rec-slider'
                  : 'rec-slider-position'
              }`}
            >
              <CustomCarousel
                breakPoints={breakPoints}
                currentItemIndex={currentItemIndex}
                setCurrentItemIndex={setCurrentItemIndex}
                goToIndex={goToIndex}
                items={
                  props.component.answer &&
                  learnerItems?.map((item, index) => (
                    <Grid
                      key={index}
                      container
                      className={screenCaptureStyles.carouselItemsContainer}
                    >
                      <Grid
                        ref={imageRef}
                        className={screenCaptureStyles.imgContainer}
                      >
                        <ClickAwayListener onClickAway={handleClickAway}>
                          <img
                            src={item.image}
                            alt={item.id}
                            className={screenCaptureStyles.imgProp}
                            onClick={() => handleImageClick(item, index)}
                          />
                        </ClickAwayListener>
                      </Grid>
                      {canEditCapture() && (
                        <Grid
                          className={screenCaptureStyles.cancelIconContainer}
                        >
                          <IconButton
                            size="small"
                            disabled={correct && (!reattempt || finalized)}
                            onClick={() => {
                              handleRemove(index);
                            }}
                          >
                            <CancelIcon
                              className={screenCaptureStyles.cancelIcon}
                            />
                          </IconButton>
                        </Grid>
                      )}
                    </Grid>
                  ))
                }
              />
            </Grid>
          )}
        </Grid>
      )}
    </>
  );
};
